import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, ClipboardList, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = [
  ["hta", "Hipertensión Arterial"],
  ["tiroides", "Alteraciones Tiroides"],
  ["ansiedad", "Ansiedad / Estrés severo"],
  ["dm", "Diabetes Mellitus"],
  ["autoinmune", "Enfermedad Autoinmune"],
  ["anemia", "Anemia / Déficit hierro"],
  ["dislipemia", "Dislipemia / Colesterol"],
  ["cardio", "Cardiopatía / Arritmias"],
  ["alopecia", "Alopecia (antec. familiar)"],
];

const emptyForm = {
  occupation: "",
  chief_complaint: "",
  history_evolution: "",
  conditions: [],
  allergies_known: false,
  allergies_detail: "",
  habitual_treatment: "",
  surgeries: "",
  smoking: "no",
  alcohol: "no",
};

export default function PreInterrogation() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/pre-interrogation/${token}`)
      .then(({ data }) => setState({ ...data }))
      .catch((err) => setState({ status: "error", detail: err?.response?.data?.detail || "Error" }));
  }, [token]);

  const toggleCond = (id) => setForm((p) => {
    const set = new Set(p.conditions || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...p, conditions: Array.from(set) };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.chief_complaint.trim()) return toast.error("Indique el motivo de consulta");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/public/pre-interrogation/${token}`, form);
      setState({ status: "done" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el formulario");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Logo size={32} withText />
        </div>
      </header>
      <main className="flex-1 py-8 px-4 pb-40">
        <div className="max-w-2xl mx-auto space-y-4" data-testid="pre-interrogation-page">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-primary" />
            <div>
              <div className="font-display text-2xl">Historia clínica pre-consulta</div>
              <div className="text-xs text-muted-foreground">Rellene estos datos antes de su próxima visita. Se guardarán de forma privada y solo el médico podrá verlos.</div>
            </div>
          </div>

          {state.status === "loading" && (
            <Card><CardContent className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Comprobando enlace...</CardContent></Card>
          )}

          {(state.status === "already_submitted" || state.status === "done") && (
            <Card><CardContent className="p-6 flex items-center gap-2 text-emerald-800 bg-emerald-50 rounded-lg" data-testid="pre-done">
              <CheckCircle2 className="w-5 h-5" /> Formulario enviado correctamente. Puede cerrar esta página.
            </CardContent></Card>
          )}

          {state.status === "expired" && (
            <Card><CardContent className="p-6 flex items-center gap-2 text-amber-800 bg-amber-50 rounded-lg">
              <AlertTriangle className="w-5 h-5" /> Este enlace ha caducado. Contacte con la consulta.
            </CardContent></Card>
          )}

          {state.status === "error" && (
            <Card><CardContent className="p-6 text-sm">Enlace no válido: {state.detail}</CardContent></Card>
          )}

          {state.status === "open" && (
            <Card>
              <CardContent className="p-6 space-y-5">
                <div className="text-sm text-muted-foreground">Paciente: <strong>{state.patient_name}</strong> · Dr. {state.doctor_name}</div>

                <form onSubmit={submit} className="space-y-5">
                  <section className="space-y-2">
                    <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">Motivo y evolución</h3>
                    <div>
                      <Label>Motivo principal de consulta *</Label>
                      <Input required value={form.chief_complaint} onChange={(e) => setForm((p) => ({ ...p, chief_complaint: e.target.value }))} data-testid="pre-chief" />
                    </div>
                    <div>
                      <Label>Historia / evolución de la enfermedad actual</Label>
                      <Textarea rows={3} value={form.history_evolution} onChange={(e) => setForm((p) => ({ ...p, history_evolution: e.target.value }))} data-testid="pre-history" />
                    </div>
                    <div>
                      <Label>Ocupación</Label>
                      <Input value={form.occupation} onChange={(e) => setForm((p) => ({ ...p, occupation: e.target.value }))} data-testid="pre-occupation" />
                    </div>
                  </section>

                  <section className="space-y-2">
                    <h3 className="font-display text-base bg-emerald-700 text-white px-3 py-1 rounded">Antecedentes y hábitos</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {CONDITIONS.map(([id, label]) => (
                        <label key={id} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input type="checkbox" checked={form.conditions.includes(id)} onChange={() => toggleCond(id)} data-testid={`pre-cond-${id}`} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="grid sm:grid-cols-[160px_1fr] gap-3">
                      <div>
                        <Label className="text-xs">Alergias conocidas</Label>
                        <div className="flex items-center gap-3 h-9">
                          <label className="flex items-center gap-1 text-sm cursor-pointer">
                            <input type="radio" checked={!form.allergies_known} onChange={() => setForm((p) => ({ ...p, allergies_known: false }))} />
                            No
                          </label>
                          <label className="flex items-center gap-1 text-sm cursor-pointer">
                            <input type="radio" checked={form.allergies_known} onChange={() => setForm((p) => ({ ...p, allergies_known: true }))} />
                            Sí
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Detalle</Label>
                        <Input value={form.allergies_detail} onChange={(e) => setForm((p) => ({ ...p, allergies_detail: e.target.value }))} disabled={!form.allergies_known} data-testid="pre-allerg-detail" />
                      </div>
                    </div>
                    <div>
                      <Label>Tratamiento habitual (medicamentos que toma)</Label>
                      <Input value={form.habitual_treatment} onChange={(e) => setForm((p) => ({ ...p, habitual_treatment: e.target.value }))} data-testid="pre-tx-hab" />
                    </div>
                    <div>
                      <Label>Cirugías previas</Label>
                      <Input value={form.surgeries} onChange={(e) => setForm((p) => ({ ...p, surgeries: e.target.value }))} data-testid="pre-surgeries" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tabaco</Label>
                        <select className="w-full h-10 border border-input rounded-md px-3" value={form.smoking} onChange={(e) => setForm((p) => ({ ...p, smoking: e.target.value }))} data-testid="pre-smoking">
                          <option value="no">No</option>
                          <option value="si">Sí</option>
                        </select>
                      </div>
                      <div>
                        <Label>Alcohol</Label>
                        <select className="w-full h-10 border border-input rounded-md px-3" value={form.alcohol} onChange={(e) => setForm((p) => ({ ...p, alcohol: e.target.value }))} data-testid="pre-alcohol">
                          <option value="no">No</option>
                          <option value="ocasional">Ocasional</option>
                          <option value="diario">Diario</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <Button type="submit" disabled={submitting} className="w-full bg-primary hover:bg-primary/90" data-testid="pre-submit">
                    {submitting ? "Enviando..." : "Enviar formulario"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
